

import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { formatCurrencyVND } from "../../utils/helps";
import DatePicker from "../DatePicker";
import ApiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";

const membershipFeeSchema = Yup.object().shape({
  title: Yup.string().trim().required("Chủ đề không được để trống"),
  fee: Yup.number()
    .positive("Hội phí phải là số dương")
    .required("Hội phí không được để trống"),
});

const payStartDaySchema = Yup.object().shape({
  payStartDay: Yup.date()
    .required("Ngày bắt đầu đóng không được để trống")
    // .min(
    //   new Date().toISOString().slice(0, 10),
    //   "Ngày bắt đầu phải đóng từ hôm nay trở đi"
    // )
    .typeError("Ngày bắt đầu đóng không hợp lệ"),
});

const fieldLabels = {
  title: "Chủ đề",
  fee: "Số tiền",
  payStartDay: "Ngày bắt đầu đóng",
};

const MembershipFeeModal = ({ allCompanies, membershipFeeId, onClose }) => {
  const [formData, setFormData] = useState({
    fee: 0.0,
    title: "",
    payStartDay: "",
    selectedCompanies: [],
  });
  const [errors, setErrors] = useState({});
  const [membershipFee, setMembershipFee] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (membershipFeeId) {
      getMembershipFee();
    }
  }, []);

  const getMembershipFee = () => {
    ApiCustom.get(`/membership-fee-shows/${membershipFeeId}`)
      .then((res) => {
        setMembershipFee(res.data?.data?.data);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (membershipFee) {
      setFormData(membershipFee);
      setFormData((prev) => {
        const selectedCompanies = membershipFee?.companies?.map(
          (company) => company.id
        );
        return { ...prev, selectedCompanies };
      });
    } else {
      handleCheckAllCompanies(true);
    }
  }, [membershipFee]);

  const handleCompanyCheckboxChange = (companyId) => {
    setFormData((prev) => {
      const isSelected = prev.selectedCompanies.includes(companyId);
      const updatedCompanies = isSelected
        ? prev.selectedCompanies.filter((id) => id !== companyId)
        : [...prev.selectedCompanies, companyId];
      return { ...prev, selectedCompanies: updatedCompanies };
    });
  };

  const handleCheckAllCompanies = (checked) => {
    if (checked) {
      const allCompanyIds = allCompanies.map((company) => company.id);
      setFormData((prev) => ({
        ...prev,
        selectedCompanies: allCompanyIds,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        selectedCompanies: [],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitLockRef.current) {
      return;
    }

    submitLockRef.current = true;
    setIsSubmitting(true);

    try {
      if (formData.fee === "") {
        formData.fee = 0;
      }

      let schema = membershipFeeSchema;
      if (
        membershipFee == null ||
        formData.payStartDay != membershipFee?.payStartDay
      ) {
        schema = membershipFeeSchema.concat(payStartDaySchema);
      }

      await schema.validate(formData, { abortEarly: false });
      if (membershipFee) {
        await ApiCustom.put(
          "/membership-fee-update/" + membershipFee.id,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
          .then((res) => {
            if (res.status === 200) {
              toast.success("Cập nhật hội phí thành công!");
              onClose();
            }
          })
          .catch((err) => {
            if (err.response?.status === 422) {
              toast.error(err?.response?.data?.messages || "Đã xảy ra lỗi!");
            } else {
              toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
            }
          });
      } else {
        await ApiCustom.post("/membership-fee-create", formData, {
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((res) => {
            if (res.status === 200) {
              toast.success("Thêm hội phí thành công!");
              onClose();
            }
          })
          .catch(() => {
            toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
          });
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
      }
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-base-100 rounded-lg shadow-xl w-2/3 max-h-[90vh] overflow-y-auto">
        <div className="">
          <div className="flex sticky top-0 bg-base-100 z-10 p-6 justify-between items-center">
            <h2 className="text-2xl font-bold ">
              {membershipFee ? "Chỉnh sửa hội phí" : "Thêm hội phí"}
            </h2>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              X
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="px-6">
              <div className="flex flex-col md:flex-row gap-5 mb-1">
                <div className="w-full">
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.title}
                    <i className="text-red-600"> *</i>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        title: e.target.value,
                      });
                      setErrors({ ...errors, title: "" });
                    }}
                    className={`w-full p-2 border rounded-md ${
                      errors.title ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>
                <div className="w-full">
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.fee}
                    <i className="text-red-600"> *</i>
                  </label>
                  <input
                    type="text"
                    name="fee"
                    value={formatCurrencyVND(formData.fee)}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, "");

                      setFormData((prev) => ({
                        ...prev,
                        fee: rawValue === "" ? "" : Number(rawValue),
                      }));

                      setErrors((prev) => ({
                        ...prev,
                        fee: "",
                      }));

                      setTimeout(() => {
                        const inputElement = e.target;
                        const formattedValue = formatCurrencyVND(
                          Number(rawValue)
                        );

                        const position = formattedValue.length - 2;
                        inputElement.setSelectionRange(position, position);
                      }, 0);
                    }}
                    className={`w-full p-2 border rounded-md ${
                      errors.fee ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  {errors.fee && (
                    <p className="mt-1 text-sm text-red-600">{errors.fee}</p>
                  )}
                </div>
                <div className="w-full mb-5">
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.payStartDay}
                    <i className="text-red-600"> *</i>
                  </label>
                  <DatePicker
                    name="payStartDay"
                    value={formData.payStartDay}
                    onChange={(name, value) => {
                      setFormData({ ...formData, payStartDay: value });
                      setErrors({ ...errors, payStartDay: "" });
                    }}
                    title={fieldLabels.payStartDay}
                    error={errors.payStartDay}
                  />
                  {errors.payStartDay && (
                    <p className="text-red-500 text-sm">{errors.payStartDay}</p>
                  )}
                </div>
              </div>

              {/* Hội viên phải đóng */}
              <div className="border-t py-4">
                <label className="block text-md font-medium mb-3">
                  Hội viên phải đóng
                </label>
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="select-all-companies"
                    checked={
                      formData.selectedCompanies?.length ===
                        allCompanies.length && allCompanies.length > 0
                    }
                    onChange={(e) => {
                      handleCheckAllCompanies(e.target.checked);
                    }}
                    className="checkbox checkbox-info"
                  />
                  <label
                    htmlFor="select-all-companies"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Chọn tất cả
                  </label>
                </div>
                <div className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {allCompanies.map((company) => (
                    <div key={company.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`company-${company.id}`}
                        checked={formData.selectedCompanies?.includes(
                          company.id
                        )}
                        onChange={() => handleCompanyCheckboxChange(company.id)}
                        className="checkbox checkbox-info"
                      />
                      <label
                        htmlFor={`company-${company.id}`}
                        className="ml-2 text-sm text-gray-700"
                      >
                        {company.companyName}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer - Action Buttons */}
            <div className="flex sticky bottom-0 bg-base-100 z-10 p-6 justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting && (
                  <span className="loading loading-spinner loading-xs"></span>
                )}
                {isSubmitting
                  ? "Đang lưu..."
                  : membershipFee
                  ? "Cập nhật"
                  : "Thêm mới"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MembershipFeeModal;


